import { Query, ResultSet } from "@cubejs-client/core";
import { AppThunk } from "appThunk";
import { PinnedLocation } from "modules/customer/tools/location/pinnedLocation";
import { cubeLoad } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";
import { SizeBucket } from "modules/customer/tools/location/sizeBucket";

export enum CompetitorClassification {
    Alternate = "Alternate Category Competitor",
    Direct = "Direct competitor",
    Indirect = "Indirect competitor",
    Standard = "Competitor"
}

export class Competitor {
    public readonly id: string;
    public readonly storeName: string;
    public readonly brandName: string;
    public readonly primaryCategoryId: number;
    public readonly latitude: number;
    public readonly longitude: number;
    public readonly sizeBucketId: number;
    public readonly isAlternateCategory: boolean;
    public readonly alternateCategoryIds: number[];

    constructor(
        id: string,
        storeName: string,
        brandName: string,
        primaryCategoryId: number,
        latitude: number,
        longitude: number,
        sizeBucketId: number,
        isAlternateCategory: boolean,
        alternateCategoryIds: number[]
    ) {
        this.id = id;
        this.storeName = storeName;
        this.brandName = brandName;
        this.primaryCategoryId = primaryCategoryId;
        this.latitude = latitude;
        this.longitude = longitude;
        this.sizeBucketId = sizeBucketId;
        this.isAlternateCategory = isAlternateCategory;
        this.alternateCategoryIds = alternateCategoryIds;
    }
}

export class ClassifiedCompetitor extends Competitor {
    public readonly classification: CompetitorClassification;
    public readonly displayCategoryName: string;
    public readonly alternateCategoryNames: string;
    public readonly distanceToSelectedLocation: number;
    public readonly sizeBucket?: SizeBucket;

    constructor(
        competitor: Competitor,
        classification: CompetitorClassification,
        displayCategoryName: string,
        alternateCategoryNames: string,
        distanceToSelectedLocation: number,
        sizeBucket?: SizeBucket,
    ) {
        super(
            competitor.id,
            competitor.storeName,
            competitor.brandName,
            competitor.primaryCategoryId,
            competitor.latitude,
            competitor.longitude,
            competitor.sizeBucketId,
            competitor.isAlternateCategory,
            competitor.alternateCategoryIds
        );
        this.classification = classification;
        this.displayCategoryName = displayCategoryName;
        this.alternateCategoryNames = alternateCategoryNames;
        this.distanceToSelectedLocation = distanceToSelectedLocation;
        this.sizeBucket = sizeBucket;
    }

    get sizeLabel(): string {
        return this.sizeBucket?.label ?? "Unknown";
    }
}

export const loadCompetitors = (
    selectedLocation: PinnedLocation | undefined,
    directCompetitorNames: string[],
    indirectCategoryIds: number[],
    accountId: string
): AppThunk<Promise<Competitor[]>> => async (dispatch) => {
    try {
        if (!selectedLocation || !directCompetitorNames || !indirectCategoryIds) {
            return [];
        }

        const allRelevantPlaceCategoryIds = [
            selectedLocation.placeCategoryId,
            ...selectedLocation.secondaryPlaceCategoryIds,
            ...indirectCategoryIds
        ];

        const query: Query = {
            dimensions: [
                "Competition_DriveTime.PlaceID",
                "Competition_DriveTime.PlaceName",
                "Competition_DriveTime.BrandName",
                "Competition_DriveTime.PlaceCategory_ID",
                "Competition_DriveTime.LAT",
                "Competition_DriveTime.LNG",
                "Competition_DriveTime.SizeBucketId",
                "Competition_DriveTime.IsAltPlaceCategory",
                "Competition_DriveTime.ClientIDArray",
                "Competition_DriveTime.AltPlaceCategories"
            ],
            filters: [{
                or: [{
                    member: "Competition_DriveTime.SelectedCategory_ID",
                    operator: "equals",
                    values: allRelevantPlaceCategoryIds.map(String)
                }, {
                    member: "Competition_DriveTime.BrandName",
                    operator: "equals",
                    values: directCompetitorNames
                }]
            }, {
                member: "Mapping_RC_RZ.RetailCentreID",
                operator: "equals",
                values: [String(selectedLocation.retailCentre.id)]
            }]
        };

        const resultSet = await dispatch(cubeLoad(query)) as unknown as ResultSet;

        const competitors: Competitor[] = resultSet.rawData()
            .filter(row => !row["Competition_DriveTime.ClientIDArray"] || !(JSON.parse(row["Competition_DriveTime.ClientIDArray"]) ?? []).includes(accountId))
            .map(row => {
                return new Competitor(
                    String(row["Competition_DriveTime.PlaceID"]),
                    row["Competition_DriveTime.PlaceName"],
                    row["Competition_DriveTime.BrandName"] ?? "Unknown",
                    Number(row["Competition_DriveTime.PlaceCategory_ID"]),
                    Number(row["Competition_DriveTime.LAT"]),
                    Number(row["Competition_DriveTime.LNG"]),
                    row["Competition_DriveTime.SizeBucketId"],
                    Boolean(Number(row["Competition_DriveTime.IsAltPlaceCategory"])),
                    JSON.parse(`[${row["Competition_DriveTime.AltPlaceCategories"] ?? ""}]`)
                );
            });
        return competitors;
    } catch (error) {
        dispatch(logError("Error loading Competitors.", error));
        throw error;
    }
};
